import React, { useState, useEffect, useCallback } from 'react';
import { Book, BookStatus } from '../../types/Book';
import { bookService } from '../../services/bookService';
import BookList from './BookList';
import BookForm from './BookForm';
import OnlineBookReader from './OnlineBookReader';
import PageHeader from '../common/PageHeader';
import { User } from '../../services/authService';
import './BookPage.css';
import './Library.css'; // Import Library.css for book-card and reading progress styles

interface BookPageProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type BookPageTab = 'all' | 'reading' | 'completed' | 'paused' | 'not_started';

const BookPage: React.FC<BookPageProps> = ({ 
  user, 
  onClose,
  onToggleSidebar 
}) => {
  const [activeTab, setActiveTab] = useState<BookPageTab>('all');
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBook, setEditingBook] = useState<Book | undefined>();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isReadingOnline, setIsReadingOnline] = useState(false);
  const [selectedOnlineBook, setSelectedOnlineBook] = useState<Book | null>(null);

  // Load books with reading progress
  const loadBooks = useCallback(async () => {
    if (!user) {
      console.log('BookPage: No user, skipping load');
      return;
    }
    
    try {
      console.log('BookPage: Loading books with progress for user:', user.id);
      setLoading(true);
      setError(null);
      
      const response = await bookService.getBooksWithProgress();
      console.log('BookPage: Fetched books with progress:', response);
      if (response && response.books) {
        setBooks(response.books);
      } else {
        setBooks([]);
      }
    } catch (err: any) {
      console.error('BookPage: Error loading books:', err);
      setError(`Failed to load books: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Filter books by reading status
  const filteredBooks = React.useMemo(() => {
    if (activeTab === 'all') {
      return books;
    }
    
    return books.filter(book => {
      switch (activeTab) {
        case 'reading':
          // Show books that are explicitly marked as 'reading' OR have reading progress
          return book.readingStatus === 'reading' || 
                 (book.currentPage !== undefined && 
                  book.currentPage > 0 && 
                  book.totalPages !== undefined && 
                  book.currentPage < book.totalPages &&
                  book.readingStatus !== 'completed' &&
                  book.readingStatus !== 'paused' &&
                  book.readingStatus !== 'abandoned');
        case 'completed':
          return book.readingStatus === 'completed' ||
                 (book.currentPage !== undefined && 
                  book.totalPages !== undefined && 
                  book.currentPage >= book.totalPages);
        case 'paused':
          return book.readingStatus === 'paused';
        case 'not_started':
          return book.readingStatus === 'not_started' || 
                 (!book.readingStatus && 
                  (!book.currentPage || book.currentPage === 0));
        default:
          return true;
      }
    });
  }, [books, activeTab]);

  useEffect(() => {
    if (user) {
      loadBooks();
    } else {
      setBooks([]);
      setLoading(false);
      setError(null);
    }
  }, [user, loadBooks]);

  const handleAddBook = () => {
    setEditingBook(undefined);
    setShowForm(true);
  };

  const handleEditBook = (book: Book) => {
    setEditingBook(book);
    setShowForm(true);
  };

  const handleSubmitBook = async (book: Book) => {
    if (editingBook) {
      // Update existing book
      const success = await bookService.updateBook(book);
      if (success) {
        // If reading progress fields changed, update progress separately
        if (book.currentPage !== undefined || book.totalPages !== undefined || book.readingStatus) {
          await bookService.updateReadingProgress(book.id, {
            currentPage: book.currentPage,
            totalPages: book.totalPages,
            status: book.readingStatus
          });
        }
        setShowForm(false);
        setEditingBook(undefined);
        loadBooks(); // Reload to get updated progress
      }
    } else {
      // Add new book
      const { id, ...bookData } = book;
      const newBook = await bookService.addBook(bookData);
      if (newBook) {
        // Automatically add new book to favorites
        await bookService.addToFavorites(newBook.id);
        
        // If reading progress was set, update it
        if (book.currentPage !== undefined || book.totalPages !== undefined || book.readingStatus) {
          await bookService.updateReadingProgress(newBook.id, {
            currentPage: book.currentPage,
            totalPages: book.totalPages,
            status: book.readingStatus
          });
        }
        setShowForm(false);
        setEditingBook(undefined);
        loadBooks(); // Reload to get updated progress
      }
    }
  };

  const handleReadOnline = (book: Book) => {
    setSelectedOnlineBook(book);
    setIsReadingOnline(true);
  };

  const handleStopOnlineReading = () => {
    setIsReadingOnline(false);
    setSelectedOnlineBook(null);
    loadBooks(); // Reload to update progress if user was reading
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingBook(undefined);
  };

  const handleDeleteBook = async (bookId: string) => {
    const success = await bookService.deleteBook(bookId);
    if (success) {
      setBooks(prev => prev.filter(b => b.id !== bookId));
    }
  };

  const handleSearchWithFilters = async (query: string, filters: {
    status?: BookStatus;
    author?: string;
    subject?: string;
    language?: string;
    publication_year?: string;
    minRating?: number;
  }) => {
    // For now, use the regular search - could be enhanced to filter by reading status
    const results = await bookService.searchBooksWithFilters(query, filters);
    return { books: results, error: null };
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 'all':
        return '📖 My Books';
      case 'reading':
        return '📖 Currently Reading';
      case 'completed':
        return '✅ Completed Books';
      case 'paused':
        return '⏸️ Paused Books';
      case 'not_started':
        return '📚 Not Started';
      default:
        return '📖 Books';
    }
  };

  // Show online book reader when reading online
  if (isReadingOnline && selectedOnlineBook) {
    return (
      <div className="App fullscreen-reading">
        <OnlineBookReader 
          book={selectedOnlineBook}
          onClose={handleStopOnlineReading}
        />
      </div>
    );
  }

  return (
    <div className="book-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">{getTabTitle()}</h1>}
        rightActions={
          <>
            <button 
              className="page-header-icon-btn"
              onClick={() => setIsFilterDrawerOpen(true)}
              title="Search & Filter"
            >
              🔍
            </button>
            <button className="page-header-icon-btn" onClick={handleAddBook} title="Add Book">
              +
            </button>
          </>
        }
      />

      <div className="book-page-tabs">
        <button 
          className={`book-page-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <span className="tab-icon">📚</span>
          <span className="tab-label">All</span>
        </button>
        <button 
          className={`book-page-tab ${activeTab === 'reading' ? 'active' : ''}`}
          onClick={() => setActiveTab('reading')}
        >
          <span className="tab-icon">📖</span>
          <span className="tab-label">Reading</span>
        </button>
        <button 
          className={`book-page-tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          <span className="tab-icon">✅</span>
          <span className="tab-label">Completed</span>
        </button>
        <button 
          className={`book-page-tab ${activeTab === 'paused' ? 'active' : ''}`}
          onClick={() => setActiveTab('paused')}
        >
          <span className="tab-icon">⏸️</span>
          <span className="tab-label">Paused</span>
        </button>
        <button 
          className={`book-page-tab ${activeTab === 'not_started' ? 'active' : ''}`}
          onClick={() => setActiveTab('not_started')}
        >
          <span className="tab-icon">📚</span>
          <span className="tab-label">Not Started</span>
        </button>
      </div>

      <div className="book-page-content">
        <BookList
          books={filteredBooks}
          loading={loading}
          error={error}
          user={user}
          onEditBook={handleEditBook}
          onDeleteBook={handleDeleteBook}
          onReadOnline={handleReadOnline}
          onStatusChange={(book, newStatus) => {
            // Update local state and reload books
            setBooks(prev => prev.map(b => b.id === book.id ? { ...b, readingStatus: newStatus as any } : b));
            loadBooks();
          }}
          onSearchWithFilters={handleSearchWithFilters}
          selectedSubject={null}
          onClearSubject={() => {}}
          isFilterDrawerOpen={isFilterDrawerOpen}
          onFilterDrawerToggle={setIsFilterDrawerOpen}
        />
      </div>

      {showForm && (
        <BookForm
          book={editingBook}
          onSubmit={handleSubmitBook}
          onCancel={handleCancelForm}
        />
      )}
    </div>
  );
};

export default BookPage;

